import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import CodePreview from "@/components/CodePreview";
import StatementPreview from "@/components/StatementPreview";
import VariationEditor from "@/components/VariationEditor";
import StatementEditor from "@/components/StatementEditor";
import { VARIATION } from "@/constants/testIds";
import { Sparkles, Plus, Play, RefreshCw, Download, Trash2, ArrowLeft, X, Rocket } from "lucide-react";
/* X is used by the AI modal close button */
import { toast } from "sonner";

export default function CampaignDetail() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiForm, setAiForm] = useState({
    brand: "Codira", product: "Codira S1 — ad creative studio",
    audience: "marketing ops & creative teams 25–45", tone: "bold, confident, minimal",
    language: "javascript", n: 5, template_id: "",
  });

  const load = async () => {
    const [c, t] = await Promise.all([api.get(`/campaigns/${id}`), api.get("/templates")]);
    setCampaign(c.data); setTemplates(t.data);
    const firstCode = t.data.find((x) => (x.kind || "code") === "code");
    if (!aiForm.template_id && firstCode) setAiForm((f) => ({ ...f, template_id: firstCode.id }));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const submitAi = async (e) => {
    e.preventDefault();
    if (!aiForm.template_id) { toast.error("Pick a template first"); return; }
    setAiBusy(true);
    try {
      await api.post("/ai/generate-variations", {
        campaign_id: id, template_id: aiForm.template_id,
        brand: aiForm.brand, product: aiForm.product,
        audience: aiForm.audience, tone: aiForm.tone,
        language: aiForm.language, n: Number(aiForm.n) || 5,
      });
      toast.success(`Generated ${aiForm.n} variations`);
      setAiOpen(false); load();
    } catch (e) { toast.error("AI failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setAiBusy(false); }
  };

  const renderAll = async () => {
    const ids = (campaign?.variations || []).map((v) => v.id);
    if (!ids.length) return toast.error("No variations to render");
    await api.post("/renders", { variation_ids: ids });
    toast.success(`Queued ${ids.length} renders`);
  };

  const renderOne = async (vid) => {
    await api.post("/renders", { variation_ids: [vid] });
    toast.success("Queued render");
  };

  const removeVariation = async (vid) => { await api.delete(`/variations/${vid}`); load(); };

  if (!campaign) return <div className="p-8 text-gray-400 text-sm">Loading…</div>;
  const templateFor = (v) => templates.find((t) => t.id === v.template_id) || templates[0];
  const editVariation = (v) => { setEditing(v); setEditorOpen(true); };
  const editingIsStatement = editing && templateFor(editing)?.kind === "statement";

  return (
    <div className="p-8 space-y-8 max-w-[1500px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Link to="/campaigns" className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> All campaigns
          </Link>
          <div className="flex items-center gap-3 mt-2">
            <div className="w-3 h-9 rounded-sm" style={{ background: campaign.brand_color || "#ee9037" }} />
            <h1 className="font-display text-3xl text-gray-900">{campaign.name}</h1>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {campaign.objective} · {campaign.status} · {(campaign.target_platforms || []).join(" · ")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid={VARIATION.aiGenBtn} onClick={() => setAiOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-brand text-brand hover:bg-brand hover:text-white px-3.5 py-2 text-sm font-medium transition-colors">
            <Sparkles className="h-4 w-4" /> AI variations
          </button>
          <Link to={`/campaigns/${id}/new`} data-testid={VARIATION.newBtn}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 px-3.5 py-2 text-sm font-medium transition-colors">
            <Plus className="h-4 w-4" /> Blank
          </Link>
          <button data-testid={VARIATION.renderAllBtn} onClick={renderAll}
            className="btn-brand inline-flex items-center gap-2 px-4 py-2 text-sm">
            <Play className="h-4 w-4" /> Render all
          </button>
        </div>
      </div>

      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">
          Variations <span className="text-gray-400 font-normal">({campaign.variations?.length || 0})</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {(campaign.variations || []).map((v) => {
            const t = templateFor(v);
            return (
              <div key={v.id} data-testid={VARIATION.card(v.id)}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md hover:border-gray-300 transition-all group">
                <div className="bg-gray-50">
                  {t?.kind === "statement" ? (
                    <StatementPreview template={t} headline={v.headline} accent={v.accent} bullets={v.bullets} />
                  ) : (
                    <CodePreview template={t} codeLines={v.code_lines} />
                  )}
                </div>
                <div className="p-3 border-t border-gray-100">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">{v.name}</p>
                    {v.ai_generated && (
                      <span className="text-[10px] font-medium uppercase tracking-wider text-brand px-1.5 py-0.5 rounded bg-brand-50">AI</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-3 gap-1">
                    <button onClick={() => editVariation(v)}
                      className="text-xs text-gray-600 hover:text-gray-900 font-medium">Edit</button>
                    <div className="flex gap-1">
                      <button onClick={() => renderOne(v.id)} title="Render"
                        className="p-1.5 rounded-md border border-gray-200 hover:border-brand hover:text-brand transition-colors">
                        <Play className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => removeVariation(v.id)} title="Delete"
                        className="p-1.5 rounded-md border border-gray-200 hover:border-red-500 hover:text-red-500 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">
          Rendered assets <span className="text-gray-400 font-normal">({campaign.rendered_assets?.length || 0})</span>
        </h2>
        {(campaign.rendered_assets || []).length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center text-sm text-gray-500">
            No renders yet. Click <span className="text-brand font-medium">Render all</span> above.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            {campaign.rendered_assets.map((r) => (
              <div key={r.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <video src={fileUrl(`/api/renders/${r.render_job_id}/file`)}
                  controls className="w-full aspect-square bg-gray-50" />
                <div className="p-3 border-t border-gray-100 flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500">{(r.size_bytes / 1024).toFixed(0)} KB</span>
                  <div className="flex items-center gap-3">
                    <Link to={`/renders/${r.render_job_id}/publish/meta`}
                      className="text-xs text-brand hover:underline font-medium inline-flex items-center gap-1">
                      <Rocket className="h-3 w-3" /> Publish
                    </Link>
                    <a href={fileUrl(`/api/renders/${r.render_job_id}/file?download=1`)} download
                      className="text-xs text-gray-600 hover:text-brand font-medium inline-flex items-center gap-1">
                      <Download className="h-3 w-3" /> MP4
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {aiOpen && (
        <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="min-h-full flex items-start sm:items-center justify-center p-4">
            <form onSubmit={submitAi} className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-8 relative my-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
            <button type="button" onClick={() => setAiOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-900">
              <X className="h-5 w-5" />
            </button>
            <h2 className="font-display text-2xl text-gray-900">AI variations</h2>
            <p className="text-sm text-gray-500 mt-1">GPT-5.2 writes ad copy as typed code.</p>
            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Template</label>
                <select value={aiForm.template_id} onChange={(e) => setAiForm({ ...aiForm, template_id: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm bg-white">
                  {templates.filter((t) => (t.kind || "code") === "code").map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Brand</label>
                  <input data-testid={VARIATION.aiBrandInput} value={aiForm.brand} onChange={(e) => setAiForm({ ...aiForm, brand: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Language</label>
                  <select value={aiForm.language} onChange={(e) => setAiForm({ ...aiForm, language: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm bg-white">
                    <option value="javascript">JavaScript</option>
                    <option value="typescript">TypeScript</option>
                    <option value="python">Python</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Count</label>
                  <input data-testid={VARIATION.aiCountInput} type="number" min={1} max={10} value={aiForm.n} onChange={(e) => setAiForm({ ...aiForm, n: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Product / offer</label>
                <input data-testid={VARIATION.aiProductInput} value={aiForm.product} onChange={(e) => setAiForm({ ...aiForm, product: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Audience</label>
                <input data-testid={VARIATION.aiAudienceInput} value={aiForm.audience} onChange={(e) => setAiForm({ ...aiForm, audience: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Tone</label>
                <input data-testid={VARIATION.aiToneInput} value={aiForm.tone} onChange={(e) => setAiForm({ ...aiForm, tone: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm" />
              </div>
            </div>
            <button data-testid={VARIATION.aiSubmitBtn} type="submit" disabled={aiBusy}
              className="btn-brand mt-6 w-full py-2.5 text-sm flex items-center justify-center gap-2">
              {aiBusy ? <><RefreshCw className="h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4" /> Generate</>}
            </button>
          </form>
          </div>
        </div>
      )}

      {editorOpen && editingIsStatement && (
        <StatementEditor
          open={editorOpen}
          onClose={() => { setEditorOpen(false); setEditing(null); }}
          onSaved={load}
          variation={editing}
          campaignId={id}
          templates={templates}
        />
      )}
      {editorOpen && !editingIsStatement && (
        <VariationEditor
          open={editorOpen}
          onClose={() => { setEditorOpen(false); setEditing(null); }}
          onSaved={load}
          variation={editing}
          campaignId={id}
          templates={templates}
        />
      )}
    </div>
  );
}
